import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Tab,
  Tabs,
} from "@mui/material";
import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { useQuery } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumb from "../../components/Breadcrumb";
import MainLayout from "../../components/MainLayout";
import ProtectedPermissionPage from "../../components/ProtectedPermissionPage";
import useNotification from "../../hooks/useNotification";
import branch from "../../services/branch";
import queryDefault from "../../utils/queryDefault";
import PhotostripTab from "./tabs/PhotostripTab";
import RecipeBookTab from "./tabs/RecipeBookTab";
import RiwayatTab from "./tabs/RiwayatTab";

type StationDetailTab = "recipe_book" | "photostrip" | "riwayat";

export default function OpnameStationDetail() {
  const { branchId } = useParams<{ branchId: string }>();
  const navigate = useNavigate();
  const notification = useNotification();
  const [currentTab, setCurrentTab] = useState<StationDetailTab>("recipe_book");

  const { data: branchData, isLoading, error } = useQuery(
    ["opname-station-detail", branchId],
    () => branch.getDetailBranch(Number(branchId)),
    {
      ...queryDefault,
      onError: (error: any) => {
        notification.onOpen({
          message: error?.response?.data?.message || "Failed to fetch station details",
          type: "error",
          position: "top",
        });
      },
    }
  );

  const handleTabChange = (event: React.SyntheticEvent, newValue: StationDetailTab) => {
    setCurrentTab(newValue);
  };

  const renderContent = () => {
    if (!branchId) return null;

    switch (currentTab) {
      case "recipe_book":
        return <RecipeBookTab branchId={Number(branchId)} />;
      case "photostrip":
        return <PhotostripTab branchId={Number(branchId)} />;
      case "riwayat":
        return <RiwayatTab branchId={Number(branchId)} />;
      default:
        return null;
    }
  };

  if (isLoading) return <CircularProgress />;
  if (error) return null;

  const branchName = branchData?.data?.branch_name || "";

  return (
    <ProtectedPermissionPage acceptPermissions={[1, 2, 3, 13]}>
      <Helmet>
        <title>Station Detail - {branchName}</title>
      </Helmet>
      <MainLayout>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Breadcrumb
            breadcrumbs={[
              { label: "Stock Opname", href: "/app/stock-opname" },
              { label: "Station", href: "/app/stock-opname?tab=station" },
              { label: branchName },
            ]}
            label={branchName || "Station Detail"}
          />
          <Button variant="outlined" onClick={() => navigate("/app/stock-opname?tab=station")}>
            Back
          </Button>
        </Stack>

        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
          <Tabs value={currentTab} onChange={handleTabChange}>
            <Tab label="Recipe Book" value="recipe_book" />
            <Tab label="Photostrip Holder" value="photostrip" />
            <Tab label="Riwayat" value="riwayat" />
          </Tabs>
        </Box>

        {renderContent()}
      </MainLayout>
    </ProtectedPermissionPage>
  );
}
